import React, { useEffect, useMemo, useState, useRef } from "react";
import { Command } from "cmdk";
import { useQueryClient } from "@tanstack/react-query";
import {
	Building,
	LifeBuoy,
	PlusCircle,
	Search,
	User,
	Home,
	LogOut,
	Sun,
	Moon,
	Globe,
	Settings,
	CreditCard,
	ChartNoAxesCombined,
	Trash2,
} from "lucide-react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import { useAuth } from "@features/auth";
import { toast } from "sonner";
import { useDebounce } from "../../hooks/useDebounce";
import { useProperties, useDeleteProperty } from "@features/properties";
import type { PropertyResponse } from "@features/properties/schemas/property";
import {
	mortgagesApi,
	resolveMortgageCreationAccess,
} from "@/features/mortgages";
import {
	EntitlementGateModal,
	LimitPaywallModal,
	useEntitlements,
} from "@/features/subscriptions";
import { Badge } from "@hipotecar/ui/badge";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@hipotecar/ui/lib/utils";
import { safeSetItem } from "@/utils/safeStorage";

interface CommandPaletteProps {
	open: boolean;
	setOpen: (open: boolean) => void;
}

interface CommandItemConfig {
	id: string;
	title: string;
	icon: React.ElementType;
	onSelect: () => void;
	shortcut?: string;
	keepOpen?: boolean;
	actions?: React.ReactNode;
}

interface CommandGroupConfig {
	heading: string;
	items: CommandItemConfig[];
}

export default function CommandPalette({ open, setOpen }: CommandPaletteProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toggleTheme, theme } = useTheme();
	const { t, i18n } = useTranslation([
		"common",
		"propiedades",
		"pricing",
		"hipotecas",
	]);
	const { signOut } = useAuth();
	const inputRef = useRef<HTMLInputElement>(null);

	// State
	const [search, setSearch] = useState("");
	const [searchMode, setSearchMode] = useState<"default" | "properties">(
		"default",
	);
	const [isPropertyGateModalOpen, setIsPropertyGateModalOpen] = useState(false);
	const [isMortgageGateModalOpen, setIsMortgageGateModalOpen] =
		useState(false);
	const debouncedSearch = useDebounce(search, 300);

	// Data Fetching
	// Only fetch properties when in property search mode and search term is present (optional: or just open)
	// We use server-side search now.
	const { data: propertiesData } = useProperties({
		limit: 100,
		enabled:
			open && searchMode === "properties" && debouncedSearch.trim().length > 0,
		search: debouncedSearch,
	});
	const properties = useMemo(
		() => propertiesData?.data || [],
		[propertiesData?.data],
	);
	const {
		data: propertyCountData,
		isLoading: isPropertyCountLoading,
	} = useProperties({
		limit: 1,
		enabled: open,
	});
	const {
		canCreate,
		getLimit,
		isLoading: isSubscriptionLoading,
	} = useEntitlements();
	const deleteProperty = useDeleteProperty();

	
	// Toggle the menu when ⌘K is pressed
	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setOpen(!open);
			}
		};

		document.addEventListener("keydown", down);
		return () => document.removeEventListener("keydown", down);
	}, [open, setOpen]);

	// Reset search when opening
	useEffect(() => {
		if (open) {
			setSearch("");
			setSearchMode("default");
		}
	}, [open]);

	const runCommand = (command: () => void, keepOpen = false) => {
		command();
		if (!keepOpen) {
			setOpen(false);
		} else {
			// Focus back on input if keepOpen is true
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	};

	const handleSignOut = React.useCallback(async () => {
		await signOut();
		toast.success(t("buttons.logout"));
		navigate("/signin");
	}, [signOut, t, navigate]);

	const toggleLanguage = React.useCallback(() => {
		const currentLang = i18n.language || "es";
		const newLang = currentLang.startsWith("en") ? "es" : "en";
		i18n.changeLanguage(newLang);
		safeSetItem("i18nextLng", newLang);
	}, [i18n]);

	const handleDeleteProperty = React.useCallback(
		(e: React.MouseEvent, id: number, name: string) => {
			e.stopPropagation();
			if (window.confirm(t("propiedades:deleteConfirm", { name }))) {
				deleteProperty.mutate(id, {
					onSuccess: () => {
						toast.success(t("propiedades:deleteSuccess"));
					},
					onError: () => {
						toast.error(t("propiedades:deleteError"));
					},
				});
			}
		},
		[t, deleteProperty],
	);

	const propertyCount = propertyCountData?.pagination.total_count ?? 0;
	const isPropertyGateReady = !isPropertyCountLoading && !isSubscriptionLoading;

	const handleCreateProperty = React.useCallback(() => {
		if (!isPropertyGateReady) {
			return;
		}

		if (canCreate("properties", propertyCount)) {
			setOpen(false);
			navigate("/properties/create");
			return;
		}

		setOpen(false);
		setIsPropertyGateModalOpen(true);
	}, [
		canCreate,
		isPropertyGateReady,
		navigate,
		propertyCount,
		setOpen,
	]);

	const handleAddMortgage = React.useCallback(
		async (e: React.MouseEvent, propertyId: number) => {
			e.stopPropagation();
			try {
				const currentMortgageCount = await queryClient.fetchQuery({
					queryKey: ["mortgage-count", propertyId],
					queryFn: () => mortgagesApi.getCountForProperty(propertyId),
				});

				const { isGateReady, canCreateMortgage } =
					resolveMortgageCreationAccess({
						isSubscriptionLoading,
						isMortgageCountLoading: false,
						currentMortgageCount,
						canCreate,
					});

				if (!isGateReady) {
					return;
				}

				setOpen(false);

				if (!canCreateMortgage) {
					setIsMortgageGateModalOpen(true);
					return;
				}

				navigate(`/mortgages/create?propertyId=${propertyId}`, {
					state: { propertyId },
				});
			} catch (error) {
				console.error("Failed to load mortgage count:", error);
				toast.error(t("common:errors.loadFailed"));
			}
		},
		[
			canCreate,
			isSubscriptionLoading,
			navigate,
			queryClient,
			setOpen,
			t,
		],
	);

	const handleViewMortgage = React.useCallback(
		(propertyId: number) => {
			setOpen(false);
			navigate(`/properties/${propertyId}?tab=mortgages`);
		},
		[navigate, setOpen],
	);

	// Search Mode Logic
	const debouncedLower = debouncedSearch.toLowerCase();

	const isPropertySearch = searchMode === "properties";
	const propertyQuery = isPropertySearch ? debouncedLower.trim() : "";

	const commandGroups: CommandGroupConfig[] = useMemo(() => {
		// Property Search Mode
		if (isPropertySearch) {
			if (propertyQuery.length === 0) {
				return [
					{
						heading: t("common:pageTitles.properties"),
						items: [],
					},
				];
			}

			// Server-side filtered properties
			if (properties.length === 0) {
				return [
					{
						heading: t("common:pageTitles.properties"),
						items: [],
					},
				];
			}

			return [
				{
					heading: t("common:pageTitles.properties"),
					items: (properties as PropertyResponse[]).map((p) => ({
						id: `prop-${p.id}`,
						title: p.nombre,
						icon: Building,
						onSelect: () => navigate(`/properties/${p.id}`),
						actions: (
							<PropertyActions
								idHipotecaActiva={p.id_hipoteca_activa}
								onDelete={(e) => handleDeleteProperty(e, p.id, p.nombre)}
								onAdd={(e) => {
									void handleAddMortgage(e, p.id);
								}}
								onViewMortgage={(e) => {
									e.stopPropagation();
									handleViewMortgage(p.id);
								}}
							/>
						),
					})),
				},
			];
		}

		// Default Groups
		return [
			{
				heading: t("cmdk.sections.navigation"),
				items: [
					{
						id: "dashboard",
						title: t("cmdk.commands.dashboard"),
						icon: Home,
						onSelect: () => navigate("/dashboard"),
					},
					{
						id: "properties",
						title: t("common:pageTitles.properties"),
						icon: Building,
						onSelect: () => navigate("/properties"),
					},

					{
						id: "profile",
						title: t("common:pageTitles.profile"),
						icon: User,
						onSelect: () => navigate("/settings"),
					},

					{
						id: "pricing",
						title: t("cmdk.commands.pricing"),
						icon: CreditCard,
						onSelect: () => navigate("/pricing"),
					},
				],
			},
			{
				heading: t("cmdk.sections.actions"),
				items: [
					{
						id: "search-properties",
						title: t("cmdk.commands.searchProperties"),
						icon: Search,
						onSelect: () => {
							setSearchMode("properties");
							setSearch("");
						},
						keepOpen: true,
					},
					{
						id: "create-property",
						title: t("common:pageTitles.createProperty"),
						icon: PlusCircle,
						onSelect: handleCreateProperty,
					},
				],
			},
			{
				heading: t("cmdk.sections.system"),
				items: [
					{
						id: "settings",
						title: t("cmdk.commands.settings"),
						icon: Settings,
						onSelect: () => navigate("/settings"),
					},
					{
						id: "support",
						title: t("cmdk.commands.support"),
						icon: LifeBuoy,
						onSelect: () => navigate("/settings"),
					},
					{
						id: "language",
						title: `${t("cmdk.commands.toggleLanguage")} (${i18n.language.toUpperCase()})`,
						icon: Globe,
						onSelect: toggleLanguage,
					},
					{
						id: "theme",
						title: `${t("cmdk.commands.toggleTheme")} (${theme === "dark" ? t("cmdk.commands.themeLight") : t("cmdk.commands.themeDark")})`,
						icon: theme === "dark" ? Sun : Moon,
						onSelect: () => toggleTheme(),
					},
					{
						id: "logout",
						title: t("buttons.logout"),
						icon: LogOut,
						onSelect: handleSignOut,
					},
				],
			},
		];
	}, [
		navigate,
		theme,
		toggleTheme,
		t,
		i18n.language,
		handleSignOut,
		toggleLanguage,
		isPropertySearch,
		propertyQuery,
		properties,
		handleDeleteProperty,
		handleAddMortgage,
		handleCreateProperty,
		handleViewMortgage,
	]);

	const isModeActive = isPropertySearch;

	return (
		<>
			<Command.Dialog
				open={open}
				onOpenChange={setOpen}
				label={t("cmdk.label")}
				shouldFilter={!isModeActive} // Disable default filtering when in specific search modes
				className="fixed inset-0 z-999999 flex items-start justify-center bg-gray-900/40 p-0 backdrop-blur-[2px] duration-200 animate-in fade-in sm:p-4 sm:pt-[15vh]"
				onClick={() => setOpen(false)}
			>
				<DialogTitle className="sr-only">{t("cmdk.label")}</DialogTitle>
				<DialogDescription className="sr-only">
					{t("cmdk.label")}
				</DialogDescription>
				<div
					className="flex size-full max-w-xl flex-col overflow-hidden border-default bg-card shadow-theme-xl duration-300 animate-in slide-in-from-bottom-full zoom-in-95 sm:h-auto sm:rounded-2xl sm:slide-in-from-top-4 sm:zoom-in-100"
					onClick={(e) => e.stopPropagation()}
				>
					<div className="flex items-center border-b border-default px-4 py-3">
						<Search className="mr-3 size-5 text-gray-400 dark:text-gray-500" />
						{searchMode === "properties" && (
							<Badge
								variant="secondary"
								className="mr-2 rounded-sm px-1 py-0 text-xs"
							>
								{t("common:pageTitles.properties")}
							</Badge>
						)}
						<Command.Input
							ref={inputRef}
							value={search}
							onValueChange={setSearch}
							placeholder={
								searchMode === "properties"
									? t("cmdk.placeholders.properties")
									: t("cmdk.placeholder")
							}
							onKeyDown={(e) => {
								if (
									e.key === "Backspace" &&
									search.length === 0 &&
									searchMode !== "default"
								) {
									e.preventDefault();
									setSearchMode("default");
								}
							}}
							className="flex-1 border-none bg-transparent text-base font-normal text-gray-900 outline-none placeholder:text-gray-400 focus:ring-0 dark:text-white dark:placeholder:text-gray-500"
						/>
						<button
							onClick={() => setOpen(false)}
							className="ml-2 rounded-lg px-3 py-1.5 text-sm font-medium text-brand-600 transition-colors hover:bg-brand-50 active:scale-95 sm:hidden"
							aria-label={t("cmdk.keyboard.close")}
						>
							{t("cmdk.keyboard.close")}
						</button>
					</div>

					<Command.List className="no-scrollbar custom-scrollbar flex-1 overflow-y-auto p-2 sm:max-h-[400px]">
						<Command.Empty className="px-4 py-8 text-center text-sm text-secondary dark:text-gray-200">
							{searchMode === "properties" &&
							debouncedSearch.trim().length === 0
								? t("cmdk.startTyping")
								: t("cmdk.empty")}
						</Command.Empty>

						{commandGroups.map((group) => (
							<Command.Group
								key={group.heading}
								heading={group.heading}
								className="p-2 text-[11px] font-semibold tracking-wider text-tertiary uppercase"
							>
								{group.items.map((item) => (
									<Item
										key={item.id}
										onSelect={() => runCommand(item.onSelect, item.keepOpen)}
									>
										<div className="flex flex-1 items-center py-1 sm:py-0">
											<item.icon className="mr-3 size-5 text-gray-500 group-aria-selected:text-brand-600 sm:size-4 dark:text-gray-400 dark:group-aria-selected:text-white" />
											<span className="text-base font-medium sm:text-sm">
												{item.title}
											</span>
										</div>
										{item.actions}
										{item.shortcut && (
											<span className="ml-auto hidden text-xs text-tertiary sm:block">
												{item.shortcut}
											</span>
										)}
									</Item>
								))}
							</Command.Group>
						))}
					</Command.List>

					<div className="hidden items-center border-t border-default bg-gray-50 px-4 py-3 sm:flex dark:border-gray-800 dark:bg-gray-900">
						<div
							className="flex gap-4 text-[10px] font-bold tracking-widest text-gray-400 uppercase dark:text-gray-400"
							aria-label="Keyboard shortcuts"
						>
							<span className="flex items-center gap-1.5">
								<kbd className="font-sans flex min-w-[20px] items-center justify-center rounded-sm border border-gray-200 bg-white px-1.5 py-0.5 font-medium shadow-theme-xs dark:border-gray-700 dark:bg-gray-800">
									↵
								</kbd>
								{t("cmdk.keyboard.select")}
							</span>
							<span className="flex items-center gap-1.5">
								<kbd className="font-sans flex min-w-[40px] items-center justify-center rounded-sm border border-default bg-card px-1.5 py-0.5 font-medium shadow-theme-xs">
									↑↓
								</kbd>
								{t("cmdk.keyboard.navigate")}
							</span>
							<span className="flex items-center gap-1.5">
								<kbd className="font-sans flex min-w-[40px] items-center justify-center rounded-sm border border-default bg-card px-1.5 py-0.5 font-medium shadow-theme-xs">
									esc
								</kbd>
								{t("cmdk.keyboard.close")}
							</span>
						</div>
					</div>
				</div>
			</Command.Dialog>
			<LimitPaywallModal
				open={isPropertyGateModalOpen}
				onClose={() => setIsPropertyGateModalOpen(false)}
				gate="property"
			/>
			<EntitlementGateModal
				open={isMortgageGateModalOpen}
				onClose={() => setIsMortgageGateModalOpen(false)}
				gateId="mortgage"
				title={t("pricing:gates.mortgage.title")}
				description={t("pricing:gates.mortgage.description", {
					count: getLimit("mortgages_per_property") ?? 0,
				})}
				cta={t("pricing:gates.cta")}
			/>
		</>
	);
}

const Item = ({
	children,
	onSelect,
}: {
	children: React.ReactNode;
	onSelect: () => void;
}) => {
	return (
		<Command.Item
			onSelect={onSelect}
			className="group flex w-full cursor-pointer items-center rounded-lg px-3 py-2.5 text-sm text-gray-700 transition-all aria-selected:bg-brand-50 aria-selected:text-brand-600 dark:text-gray-200 dark:aria-selected:bg-white/5 dark:aria-selected:text-white"
		>
			{children}
		</Command.Item>
	);
};

const PropertyActions = ({
	onDelete,
	onAdd,
	onViewMortgage,
	idHipotecaActiva,
}: {
	idHipotecaActiva?: number | null;
	onDelete: (e: React.MouseEvent) => void;
	onAdd: (e: React.MouseEvent) => void;
	onViewMortgage: (e: React.MouseEvent) => void;
}) => {
	const { t } = useTranslation(["propiedades", "common", "hipotecas"]);
	const activeMortgageId = idHipotecaActiva ?? null;

	return (
		<div className="ml-auto flex items-center gap-2">
			<button
				onClick={(e) => {
					if (activeMortgageId) {
						onViewMortgage(e);
					} else {
						onAdd(e);
					}
				}}
				className={cn(
					"p-1 transition-colors",
					activeMortgageId
						? "text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
						: "text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300",
				)}
				title={
					activeMortgageId
						? t("hipotecas:show.title")
						: t("hipotecas:create.create_trigger")
				}
			>
				<ChartNoAxesCombined className="size-4" />
			</button>
			<button
				onClick={onDelete}
				className="p-1 text-gray-400 transition-colors hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
				title={t("common:actions.delete")}
			>
				<Trash2 className="size-4" />
			</button>
		</div>
	);
};
